/**
 * PATH: apps/quote/src/hooks/useOrganisationDefaults.ts
 * FILE_ID: QB-HOOK-ORG-DEFAULTS-001
 * PURPOSE: Read-side hook that resolves the active organisation's
 *          tenant-level defaults (currently just `default_labour_rate`)
 *          for surfacing inside the Quote Builder's AssumptionsSheet.
 *
 * WHY: `Quote.get_default_config()` on the backend hardcodes
 *      `labourCostRate: 134.00` for every new quote across every
 *      tenant. Bravada's real median is $121/hr; SAM's is different
 *      again. Estimators were manually retyping the rate on every
 *      quote — exactly the pain that motivated MR #3a. This hook
 *      lets the AssumptionsSheet surface the org's value as a
 *      one-click "use org default" button, without touching the
 *      per-quote `Quote.config` write path or the backend defaults
 *      that SAM is currently relying on.
 *
 * FLOW:
 *   ┌────────────────────┐    ┌─────────────────────┐    ┌────────────────┐
 *   │ GET /api/          │ -> │ find org matching   │ -> │ defaultLabour  │
 *   │ organisations/     │    │ user.organisation_  │    │ Rate : number  │
 *   │ (paginated list)   │    │ id                  │    │ | null         │
 *   └────────────────────┘    └─────────────────────┘    └────────────────┘
 *
 * DEDUP: Shares the TanStack Query cache with
 *        `useQuoteBuilderTradeMode` via the identical queryKey
 *        ['quote-builder-trade-mode', 'org-list']. Both hooks can
 *        mount on the same page (Setup tab + AssumptionsSheet)
 *        without firing two GETs.
 *
 * RELATED:
 *   - QB-HOOK-TRADEMODE-001 — sibling hook that reads
 *     `quote_builder_mode` from the same list response
 *   - organisation.serializers.base.OrganisationSerializer — backend
 *     contract; `default_labour_rate` added in companion MR #2882
 *   - apps/quote/src/components/builder/sheets/AssumptionsSheet.tsx
 *     — primary consumer
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@quotech/auth'

import { getApiClient } from '@/lib/setupApi'

interface OrganisationListEntry {
  id: number | string
  // Tenant-level baseline labour rate ($/hour). Stringified Decimal
  // on the wire (DRF default Decimal handling). Null when the org
  // pre-dates the OrganisationSettings table.
  default_labour_rate?: string | number | null
}

interface OrganisationListResponse {
  results?: OrganisationListEntry[]
}

/**
 * Coerce the serialized `default_labour_rate` (string | number | null)
 * into a finite positive number, or null when unusable.
 *
 * I→O. Pure, allocation-light: called inside `useMemo` so it must not
 * surprise React with a new object identity when the input is null.
 *
 * Rejected (returns null):
 *   - undefined / null / ''
 *   - non-finite (NaN, Infinity, -Infinity)
 *   - zero or negative (a labour rate of $0/hr is operator error, not
 *     a meaningful "use org default")
 */
export function parseDefaultLabourRate(
  raw: string | number | null | undefined,
): number | null {
  if (raw === null || raw === undefined || raw === '') return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n)) return null
  if (n <= 0) return null
  return n
}

/**
 * Find the org row whose id matches the active user's organisation_id
 * and return its parsed `default_labour_rate`.
 *
 * I→O. Returns null when:
 *   - orgs list not yet loaded (`undefined`)
 *   - no `organisationId` available (anonymous / org-switching mid-flight)
 *   - no matching org row found
 *   - matching row has null / unusable rate
 */
export function findDefaultLabourRateForOrg(
  orgs: OrganisationListEntry[] | undefined,
  organisationId: string | number | null | undefined,
): number | null {
  if (!orgs || organisationId == null) return null
  const wanted = String(organisationId)
  const match = orgs.find((o) => String(o.id) === wanted)
  return match ? parseDefaultLabourRate(match.default_labour_rate) : null
}

interface UseOrganisationDefaultsResult {
  /**
   * Org's default labour rate ($/hr) if known, else null. Consumers
   * MUST treat null as "no org default to apply" — show no button,
   * keep the per-quote value as-is.
   */
  defaultLabourRate: number | null
  isLoading: boolean
  error: unknown
}

/**
 * Resolve the active organisation's tenant-level defaults.
 *
 * Backed by TanStack Query; shares the cache with the trade-mode
 * hook via an identical queryKey (intentional — both hooks read the
 * same `/organisations/` response). Long staleTime because labour
 * rates change maybe once a year per tenant.
 */
export function useOrganisationDefaults(): UseOrganisationDefaultsResult {
  const { user } = useAuth()
  const organisationId = user?.organisation_id ?? null

  const query = useQuery({
    // SAME KEY as useQuoteBuilderTradeMode — TanStack Query dedupes
    // by exact key match, so this hook does NOT fire a second GET
    // when the Setup tab and AssumptionsSheet are both mounted.
    queryKey: ['quote-builder-trade-mode', 'org-list'],
    queryFn: async (): Promise<OrganisationListEntry[]> => {
      const apiClient = getApiClient()
      const { data } = await apiClient.get<
        OrganisationListResponse | OrganisationListEntry[]
      >('/organisations/')
      if (Array.isArray(data)) return data
      return data?.results ?? []
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!organisationId,
  })

  const defaultLabourRate = useMemo(
    () => findDefaultLabourRateForOrg(query.data, organisationId),
    [query.data, organisationId],
  )

  return {
    defaultLabourRate,
    isLoading: query.isLoading,
    error: query.error,
  }
}
